import React, { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from "react";

import { captchaSiteKey } from "../../config";
import useTheme from "../../hooks/useTheme";

interface RecaptchaConfig {
  sitekey: string;
  theme?: "light" | "dark";
  size?: "compact" | "normal" | "invisible";
  badge?: "bottomright" | "bottomleft" | "inline";
  tabindex?: number;
  hl?: string;
  isolated?: boolean;
  callback?: (response: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
}

interface ReCaptchaInstance {
  ready: (callback: () => void) => void;
  render: (container?: HTMLElement | null, config?: RecaptchaConfig) => number;
  reset: (id?: number) => void;
  execute: (id?: number) => void;
  getResponse: (id?: number) => string;
}

declare global {
  interface Window {
    grecaptcha: ReCaptchaInstance;
    captchaCallback: (result: string) => void;
    recaptchaOnload: () => void;
  }
}

type ReCaptchaContext = {
  execute: () => Promise<string | undefined>;
  isScriptLoaded: boolean;
};

export const ReCAPTCHAContext = React.createContext<ReCaptchaContext>({
  execute: () => Promise.resolve(undefined),
  isScriptLoaded: false,
} satisfies ReCaptchaContext);

const ReCAPTCHAProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme] = useTheme();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const resultResolve = useRef<(result: string) => void>(undefined);

  const cancelObserver = useMemo(() => {
    return new MutationObserver(() => {
      const iframe = document.querySelector('iframe[title*="recaptcha"]');

      if (iframe) {
        cancelObserver.disconnect();
        const captchaBackground = iframe.parentNode?.parentNode?.firstChild;
        const clickEvent = () => {
          window.captchaCallback("cancel");
        };
        captchaBackground?.removeEventListener("click", clickEvent);
        captchaBackground?.addEventListener("click", clickEvent);
      }
    });
  }, []);

  const execute = async () => {
    if (!window.grecaptcha) return undefined;
    window.grecaptcha.reset();

    const promise = new Promise<string | undefined>((resolve) => {
      resultResolve.current = (res: string) => {
        resolve(res);
      };
    });

    window.captchaCallback = (result) => {
      resultResolve.current?.(result);
    };

    cancelObserver.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
    });

    window.grecaptcha.execute();

    return promise;
  };

  const recaptchaOnload = useCallback(() => {
    const recaptchaRoot = document.querySelector("#recaptcha-root") as HTMLElement;
    if (recaptchaRoot.childNodes.length === 0) {
      setIsScriptLoaded(true);

      window.grecaptcha.render(recaptchaRoot, {
        sitekey: captchaSiteKey,
        size: "invisible",
        badge: "bottomright",
        callback: (result) => resultResolve.current?.(result),
        "error-callback": () => {
          resultResolve.current?.("");
        },
        theme,
      });
    }
  }, [theme]);

  useEffect(() => {
    window.recaptchaOnload = recaptchaOnload;
  }, [recaptchaOnload]);

  return (
    <ReCAPTCHAContext.Provider value={{ execute, isScriptLoaded }}>
      {children}
    </ReCAPTCHAContext.Provider>
  );
};
export default ReCAPTCHAProvider;
